import { IMG } from "@/data/site";
import FinalCTA from "@/components/FinalCTA";
import { ButtonLink, Eyebrow, FadeIn, PageHero, usePageMeta } from "@/components/blocks";

const DISCIPLINES = [
  {
    n: "01",
    title: "HYROX Preparation",
    copy: "Race-specific strength, engine work, station efficiency and pacing strategy — programmed around your event date.",
  },
  {
    n: "02",
    title: "Marathon & Endurance",
    copy: "Run durability, aerobic development and the strength work most runners skip — until it costs them.",
  },
  {
    n: "03",
    title: "Strength & Conditioning",
    copy: "Structured S&C for recreational and competitive athletes who want a physical edge that holds up.",
  },
  {
    n: "04",
    title: "Sports-Specific Training",
    copy: "Movement quality, power and resilience built around the demands of your sport — not a generic template.",
  },
];

const RACE_CYCLE = [
  { phase: "Base", note: "Build capacity, movement quality and structural resilience." },
  { phase: "Build", note: "Layer race-specific strength and conditioning volume." },
  { phase: "Peak", note: "Sharpen intensity, strategy and race-pace execution." },
  { phase: "Race", note: "Taper, fuel and execute the plan." },
  { phase: "Recover", note: "Deload, restore and set the next target." },
];

export default function Performance() {
  usePageMeta(
    "Performance Coaching — HYROX, Marathon & Sport | StrengthLeap",
    "StrengthLeap performance coaching: HYROX preparation, marathon coaching, strength & conditioning, race strategy and recovery programming."
  );

  return (
    <main>
      <PageHero
        testid="performance-page"
        eyebrow="Performance / HYROX"
        lines={["Train for more", "than the finish line."]}
        copy="Live biomechanical analysis, periodized conditioning and race strategy — coached on camera in real time, built around your event."
      />

      <section className="bg-ink px-6 pb-28 text-bone sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1440px]">
          {DISCIPLINES.map((d, i) => (
            <FadeIn key={d.n}>
              <div
                data-testid={`discipline-${d.n}`}
                className="group grid gap-4 border-t border-bone/15 py-12 transition-colors last:border-b sm:grid-cols-[120px_1fr_1.2fr] sm:items-baseline sm:gap-10"
              >
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-lime">{d.n}</p>
                <h2 className="font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">{d.title}</h2>
                <p className="max-w-lg text-base leading-relaxed text-ash">{d.copy}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-bone/10 bg-ink">
        <div className="absolute inset-0">
          <img src={IMG.runner} alt="Distance runner training" loading="lazy" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/60 to-ink" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-28 sm:px-10 lg:px-16">
          <FadeIn>
            <Eyebrow>The Race Cycle</Eyebrow>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h2 className="mt-8 font-display text-4xl font-black uppercase tracking-tight text-bone sm:text-6xl">
              Periodized. <span className="text-ash">Not improvised.</span>
            </h2>
          </FadeIn>
          <div className="mt-16 grid gap-px border border-bone/20 bg-bone/20 sm:grid-cols-2 lg:grid-cols-5">
            {RACE_CYCLE.map((r, i) => (
              <FadeIn key={r.phase} delay={i * 0.08} className="card-lift border border-white/5 bg-coal p-7">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-lime">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-5 font-display text-xl font-black uppercase tracking-tight text-bone">{r.phase}</p>
                <p className="mt-3 text-sm leading-relaxed text-ash">{r.note}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink px-6 py-24 text-bone sm:px-10 lg:px-16">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <FadeIn>
            <h2 className="max-w-2xl font-display text-3xl font-black uppercase tracking-tight sm:text-5xl">
              Bring a target. <span className="text-lime">We’ll build the program.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.15}>
            <ButtonLink to="/start" variant="lime" testid="performance-cta-assessment">
              Start Your Assessment
            </ButtonLink>
          </FadeIn>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
